import { useState } from "react";
import type { LucideIcon } from "lucide-react";
import { Button, Icons, PocketRow, PocketSection } from "@/components/ui";
import type { VocabularySuggestionKind } from "@/api/voice-vocabulary";
import { useT } from "@/i18n";
import { settingsVoiceVocabularyMessages } from "@/i18n/messages/settings-voice-vocabulary";
import { useDictationVocabularyStore } from "@/stores/dictation-vocabulary-store";
import { SettingsFieldError } from "./settings-text-field";

const SHOWN = 3;

const KINDS: Record<
  VocabularySuggestionKind,
  {
    label: Extract<
      keyof typeof settingsVoiceVocabularyMessages.en,
      `kind${string}`
    >;
    icon: LucideIcon;
  }
> = {
  person: { label: "kindPerson", icon: Icons.User },
  project: { label: "kindProject", icon: Icons.Folder },
  company: { label: "kindCompany", icon: Icons.Building2 },
};

/** People, projects and companies from Memory that are not in the list yet:
 *  suggestions only, used once added. A dismissed name never comes back. */
export function VoiceVocabularySuggestions() {
  const t = useT(settingsVoiceVocabularyMessages);
  const suggestions = useDictationVocabularyStore((state) => state.suggestions);
  const addSuggestion = useDictationVocabularyStore(
    (state) => state.addSuggestion,
  );
  const dismissSuggestion = useDictationVocabularyStore(
    (state) => state.dismissSuggestion,
  );
  const [error, setError] = useState<string | null>(null);
  if (suggestions.length === 0) return null;

  const run = (action: Promise<unknown>, message: string) => {
    setError(null);
    action.catch(() => setError(message));
  };

  return (
    <PocketSection
      title={t("fromMemory")}
      testId="voice-vocabulary-suggestions"
    >
      {suggestions.slice(0, SHOWN).map((suggestion, index) => {
        const kind = KINDS[suggestion.kind] ?? KINDS.project;
        const KindIcon = kind.icon;
        return (
          <PocketRow
            key={`${suggestion.kind}:${suggestion.name}`}
            testId={`voice-vocabulary-suggestion-${index}`}
            leading={<KindIcon className="h-4 w-4" aria-hidden="true" />}
            title={suggestion.name}
            secondLine={t(kind.label)}
            hoverActions={
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    run(
                      dismissSuggestion(suggestion.name),
                      t("hideSuggestionFailed"),
                    )
                  }
                  data-testid={`voice-vocabulary-suggestion-dismiss-${index}`}
                >
                  {t("notNeeded")}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    run(addSuggestion(suggestion.name), t("addWordFailed"))
                  }
                  data-testid={`voice-vocabulary-suggestion-add-${index}`}
                >
                  {t("add")}
                </Button>
              </>
            }
          />
        );
      })}
      <SettingsFieldError
        error={error}
        testId="voice-vocabulary-suggestions-error"
      />
    </PocketSection>
  );
}
